import { ArrowRight, BadgeCheck, MapPin, Radar, Route } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LaunchForm } from '../components/LaunchForm';
import { CTASection, PageHero, PrototypeNotice, SectionIntro } from '../components/Sections';
import { rideCategories } from '../data';

export function Rides() {
  return (
    <>
      <PageHero
        eyebrow="City and intercity rides"
        title="Choose the journey and category you asked for."
        text="Sawwari supports city and city-to-city travel where enabled, with category-specific matching and clear availability messaging when the requested option cannot be found."
        tone="ink"
      >
        <Link className="button button--light" to="#ride-options">Explore ride options</Link>
      </PageHero>

      <section className="section ride-intro">
        <div className="container ride-intro__grid">
          <div>
            <SectionIntro
              eyebrow="Request with clarity"
              title="Your category stays your category."
              text="Sawwari searches for the requested category, explains when it is unavailable, and lets the passenger choose what to do next without silently substituting another category."
            />
            <div className="mini-feature-grid">
              <article><MapPin /><strong>Set your route</strong><p>Start with clear pickup and destination details.</p></article>
              <article><Radar /><strong>Progressive search</strong><p>Expand the search area in controlled stages.</p></article>
              <article><BadgeCheck /><strong>Review the match</strong><p>See the available Captain and vehicle details.</p></article>
              <article><Route /><strong>Follow the journey</strong><p>Keep route and support information connected.</p></article>
            </div>
          </div>
          <LaunchForm />
        </div>
      </section>

      <section className="section section--soft" id="ride-options">
        <div className="container">
          <SectionIntro
            eyebrow="Ride categories"
            title="A choice for city and city-to-city movement."
            text="Availability depends on the city, route, vehicle, category and locally applicable operating requirements."
          />
          <div className="category-grid">
            {rideCategories.map((category) => {
              const Icon = category.icon;
              return (
                <article className={`category-card tone-${category.tone}`} key={category.name}>
                  <span><Icon /></span>
                  <h3>{category.name}</h3>
                  <p>{category.summary}</p>
                  <small>Availability by area</small>
                </article>
              );
            })}
          </div>
          <PrototypeNotice>
            Women-focused variants, vehicle eligibility, fares and route coverage are shown only where the relevant operating configuration is enabled.
          </PrototypeNotice>
        </div>
      </section>

      <section className="section matching-section">
        <div className="container matching-grid">
          <div>
            <span className="eyebrow">Matching principle</span>
            <h2>One transparent request flow.</h2>
            <p>The requested category remains unchanged throughout the search.</p>
          </div>
          <div className="matching-steps">
            <article><span>01</span><div><strong>Search within 2 km</strong><p>Begin with the closest eligible Captains in the requested category.</p></div></article>
            <article><span>02</span><div><strong>Expand to 5 km</strong><p>Widen the search once when the first stage has no suitable match.</p></div></article>
            <article><span>03</span><div><strong>Explain the outcome</strong><p>Show a category-specific message and clear next choices.</p></div></article>
          </div>
        </div>
      </section>

      <CTASection
        title="Move within your city or travel city to city."
        text="Service availability follows live maps, dispatch, fare, payment and local operating configuration."
        primary={{ label: 'Explore safety', to: '/safety' }}
        secondary={{ label: 'See all services', to: '/services' }}
      />
    </>
  );
}
